// src/features/roles/rolesSlice.ts
import { createSelector, createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { RootState } from '@/store/root-state';
import {
  makeRoleKey,
  makeScopeKey,
  makeScopeKeyExplicit,
  RoleScopeType,
  type IRole,
  type RoleKey,
  type RolesScopeKey,
} from '@/domain/entities/role';
import { type RolesState } from './rolesSliceTypes';
import { createRole, deleteRole, fetchRoles, updateRole } from './rolesThunks';
import { logoutUser } from '@/features/auth/authThunks';
import { updateOrganizationMember } from '@/features/organizations/organizationsThunks';

const initialState: RolesState = {
  byKey: {},
  keysByScope: {},
  currentRole: null,
  currentRoleLoading: false,
  currentRoleError: null,

  isListingByScope: {},
  errorByScope: {},
  hasFetchedByScope: {},

  isSaving: false,
  saveError: null,

  isDeleting: false,
  deleteError: null,
};

// =============================
// Internal helpers (reducers)
// =============================
function upsertOne(state: RolesState, role: IRole) {
  const key = makeRoleKey(role);
  state.byKey[key] = role;

  const scopeKey = makeScopeKey(role);
  const existing = state.keysByScope[scopeKey] ?? [];
  if (!existing.includes(key)) state.keysByScope[scopeKey] = [key, ...existing];
}

function upsertManyReplaceScope(state: RolesState, scopeKey: RolesScopeKey, roles: IRole[]) {
  const keys: RoleKey[] = roles.map(makeRoleKey);

  for (let i = 0; i < roles.length; i++) {
    state.byKey[keys[i]] = roles[i];
  }

  state.keysByScope[scopeKey] = keys;
  state.hasFetchedByScope[scopeKey] = true;
  state.isListingByScope[scopeKey] = false;
  state.errorByScope[scopeKey] = null;
}

function upsertManyMergeScope(state: RolesState, scopeKey: RolesScopeKey, roles: IRole[]) {
  const existing = state.keysByScope[scopeKey] ?? [];
  const next = [...existing];

  for (const role of roles) {
    const key = makeRoleKey(role);
    state.byKey[key] = role;
    if (!next.includes(key)) next.unshift(key);
  }

  state.keysByScope[scopeKey] = next;
}

function removeByKey(state: RolesState, roleKey: RoleKey) {
  delete state.byKey[roleKey];

  for (const scopeKey of Object.keys(state.keysByScope) as RolesScopeKey[]) {
    state.keysByScope[scopeKey] = (state.keysByScope[scopeKey] ?? []).filter((k) => k !== roleKey);
    if (state.keysByScope[scopeKey].length === 0) {
      delete state.keysByScope[scopeKey];
      delete state.isListingByScope[scopeKey];
      delete state.errorByScope[scopeKey];
      // leave hasFetchedByScope as-is; optional:
      // delete state.hasFetchedByScope[scopeKey];
    }
  }
}

// =============================
// Slice
// =============================
const rolesSlice = createSlice({
  name: 'roles',
  initialState,
  reducers: {
    clearRoles: () => ({ ...initialState }),

    // Clear one scope cache (useful if org changes, or after admin edits)
    resetScope: (state, action: PayloadAction<{ scopeKey: RolesScopeKey }>) => {
      const { scopeKey } = action.payload;
      //   const keys = state.keysByScope[scopeKey] ?? [];

      // Optionally keep role objects (they may be referenced elsewhere),
      // but if you want to truly purge them:
      // keys.forEach((k) => delete state.byKey[k]);

      delete state.keysByScope[scopeKey];
      delete state.isListingByScope[scopeKey];
      delete state.errorByScope[scopeKey];
      delete state.hasFetchedByScope[scopeKey];
    },

    // Local upsert (optimistic UI / admin edits)
    upsertRole: (state, action: PayloadAction<IRole>) => {
      upsertOne(state, action.payload);
    },

    upsertRolesReplaceScope: (state, action: PayloadAction<{ scopeKey: RolesScopeKey; roles: IRole[] }>) => {
      upsertManyReplaceScope(state, action.payload.scopeKey, action.payload.roles);
    },

    removeRoleByKey: (state, action: PayloadAction<RoleKey>) => {
      removeByKey(state, action.payload);
    },

    // Optional flags if you want to set loading state without thunks yet
    setScopeListing: (state, action: PayloadAction<{ scopeKey: RolesScopeKey; listing: boolean }>) => {
      const { scopeKey, listing } = action.payload;
      state.isListingByScope[scopeKey] = listing;
      if (listing) state.errorByScope[scopeKey] = null;
    },

    setScopeError: (state, action: PayloadAction<{ scopeKey: RolesScopeKey; error: string | null }>) => {
      const { scopeKey, error } = action.payload;
      state.errorByScope[scopeKey] = error;
      if (error) state.isListingByScope[scopeKey] = false;
    },

    setSaving: (state, action: PayloadAction<{ saving: boolean; error?: string | null }>) => {
      state.isSaving = action.payload.saving;
      if (typeof action.payload.error !== 'undefined') state.saveError = action.payload.error;
      if (action.payload.saving) state.saveError = null;
    },

    setDeleting: (state, action: PayloadAction<{ deleting: boolean; error?: string | null }>) => {
      state.isDeleting = action.payload.deleting;
      if (typeof action.payload.error !== 'undefined') state.deleteError = action.payload.error;
      if (action.payload.deleting) state.deleteError = null;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchRoles.pending, (state, action) => {
        const { scopeType, scopeID, roleKey } = action.meta.arg;
        const scopeKey = makeScopeKeyExplicit(scopeType, scopeID);

        state.currentRoleLoading = true;
        state.currentRoleError = null;
        if (!roleKey) {
          state.isListingByScope[scopeKey] = true;
          state.errorByScope[scopeKey] = null;
        }
      })
      .addCase(fetchRoles.fulfilled, (state, action) => {
        const { scopeType, scopeID, roleKey } = action.meta.arg;
        const scopeKey = makeScopeKeyExplicit(scopeType, scopeID);
        const fetchedRoles = action.payload.roles;

        state.currentRoleLoading = false;
        state.currentRoleError = null;
        state.currentRole = roleKey ? (fetchedRoles[0] ?? null) : null;

        if (roleKey) {
          upsertManyMergeScope(state, scopeKey, fetchedRoles);
          // Defensive: if scope roles were already fetched once, never leave listing stuck.
          if (state.hasFetchedByScope[scopeKey]) {
            state.isListingByScope[scopeKey] = false;
          }
        } else {
          upsertManyReplaceScope(state, scopeKey, fetchedRoles);
        }
      })
      .addCase(fetchRoles.rejected, (state, action) => {
        const { scopeType, scopeID, roleKey } = action.meta.arg;
        const scopeKey = makeScopeKeyExplicit(scopeType, scopeID);

        state.currentRoleLoading = false;
        state.currentRoleError = action.payload?.message || action.error.message || 'Failed to fetch roles';
        if (!roleKey) {
          // Mark the initial scope load as attempted so pages can surface the error
          // without auto-dispatching another fetch on every idle transition.
          state.hasFetchedByScope[scopeKey] = true;
          state.isListingByScope[scopeKey] = false;
          state.errorByScope[scopeKey] = action.payload?.message || action.error.message || 'Failed to fetch roles';
        } else if (state.hasFetchedByScope[scopeKey]) {
          // Defensive: avoid stale loading flags after scoped role refresh failures.
          state.isListingByScope[scopeKey] = false;
        }
      })

      .addCase(createRole.pending, (state) => {
        state.isSaving = true;
        state.saveError = null;
      })
      .addCase(createRole.fulfilled, (state, action) => {
        state.isSaving = false;
        state.saveError = null;
        const created = action.payload.data;
        if (created) upsertOne(state, created);
      })
      .addCase(createRole.rejected, (state, action) => {
        state.isSaving = false;
        state.saveError = action.payload?.message || action.error.message || 'Failed to create role';
      })

      .addCase(updateRole.pending, (state) => {
        state.isSaving = true;
        state.saveError = null;
      })
      .addCase(updateRole.fulfilled, (state, action) => {
        state.isSaving = false;
        state.saveError = null;
        upsertOne(state, action.payload);
      })
      .addCase(updateRole.rejected, (state, action) => {
        state.isSaving = false;
        state.saveError = action.payload?.message || action.error.message || 'Failed to update role';
      })

      .addCase(deleteRole.pending, (state) => {
        state.isDeleting = true;
        state.deleteError = null;
      })
      .addCase(deleteRole.fulfilled, (state, action) => {
        state.isDeleting = false;
        state.deleteError = null;
        removeByKey(state, `${action.payload.PK}|${action.payload.SK}`);
      })
      .addCase(deleteRole.rejected, (state, action) => {
        state.isDeleting = false;
        state.deleteError = action.payload?.message || action.error.message || 'Failed to delete role';
      })

      .addCase(updateOrganizationMember.fulfilled, (state, action) => {
        const createdRole = action.payload.createdRole;
        const deletedRole = action.payload.deletedRole;
        const deletedRoleStatus = action.payload.deletedRoleStatus;

        if (createdRole) upsertOne(state, createdRole);
        if (deletedRole && deletedRoleStatus === 'deleted') {
          removeByKey(state, makeRoleKey(deletedRole));
        }
      })

      .addCase(logoutUser.fulfilled, () => ({ ...initialState }));
  },
});

export const {
  clearRoles,
  resetScope,
  upsertRole,
  upsertRolesReplaceScope,
  removeRoleByKey,
  setScopeListing,
  setScopeError,
  setSaving,
  setDeleting,
} = rolesSlice.actions;

export default rolesSlice.reducer;

// =============================
// Selectors
// =============================
const selectRolesState = (state: RootState) => state.roles;

export const selectRoleByKey = (roleKey: RoleKey) => createSelector(selectRolesState, (s) => s.byKey[roleKey] ?? null);

export const selectRolesByScope = (scopeKey: RolesScopeKey) =>
  createSelector(selectRolesState, (s) => {
    const keys = s.keysByScope[scopeKey] ?? [];
    return keys.map((k) => s.byKey[k]).filter(Boolean);
  });

export const selectHasFetchedRolesForScope = (scopeKey: RolesScopeKey) =>
  createSelector(selectRolesState, (s) => Boolean(s.hasFetchedByScope[scopeKey]));

export const selectRolesLoadingForScope = (scopeKey: RolesScopeKey) =>
  createSelector(selectRolesState, (s) => Boolean(s.isListingByScope[scopeKey]));

export const selectRolesErrorForScope = (scopeKey: RolesScopeKey) =>
  createSelector(selectRolesState, (s) => s.errorByScope[scopeKey] ?? null);

export const selectRolesSaving = createSelector(selectRolesState, (s) => s.isSaving);
export const selectRolesSaveError = createSelector(selectRolesState, (s) => s.saveError);
export const selectRolesDeleting = createSelector(selectRolesState, (s) => s.isDeleting);
export const selectRolesDeleteError = createSelector(selectRolesState, (s) => s.deleteError);
export const selectCurrentRole = createSelector(selectRolesState, (s) => s.currentRole);
export const selectCurrentRoleLoading = createSelector(selectRolesState, (s) => s.currentRoleLoading);
export const selectCurrentRoleError = createSelector(selectRolesState, (s) => s.currentRoleError);

export const selectOrgRolesByOrgId = (orgId?: string) =>
  createSelector(selectRolesState, (s) => {
    if (!orgId) return [];
    const scopeKey = makeScopeKeyExplicit(RoleScopeType.ORG, orgId);
    const keys = s.keysByScope[scopeKey] ?? [];
    return keys.map((k) => s.byKey[k]).filter(Boolean);
  });

export const selectOrgRolesLoadingByOrgId = (orgId?: string) =>
  createSelector(selectRolesState, (s) => {
    if (!orgId) return false;
    const scopeKey = makeScopeKeyExplicit(RoleScopeType.ORG, orgId);
    // Only block the roles tab on the first scope fetch.
    return !!s.isListingByScope[scopeKey] && !s.hasFetchedByScope[scopeKey];
  });

export const selectOrgRolesErrorByOrgId = (orgId?: string) =>
  createSelector(selectRolesState, (s) => {
    if (!orgId) return null;
    const scopeKey = makeScopeKeyExplicit(RoleScopeType.ORG, orgId);
    return s.errorByScope[scopeKey] ?? null;
  });

export const selectHasFetchedOrgRolesByOrgId = (orgId?: string) =>
  createSelector(selectRolesState, (s) => {
    if (!orgId) return false;
    const scopeKey = makeScopeKeyExplicit(RoleScopeType.ORG, orgId);
    return !!s.hasFetchedByScope[scopeKey];
  });
